import React, { useState, useRef } from 'react';
import {
  StyledAudioPlayer,
  StyledButton,
  StyledSlider,
  StyledSpacer,
} from './AudioPlayerComponent.styles';

// Icons extracted for clarity
const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '2px' }}>
    <path
      d="M5 3L19 12L5 21V3Z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const PauseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" />
    <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" />
  </svg>
);

interface AudioPlayerProps {
  url: string;
  defaultPlayer?: boolean;
}

const AudioPlayerComponent = ({ url, defaultPlayer = false }: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const togglePlay = async (): Promise<void> => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Playback failed:', error);
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (audio?.duration) {
      setProgress((audio.currentTime / audio.duration) * 100);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (audio?.duration) {
      const value = Number(e.target.value);
      audio.currentTime = (audio.duration / 100) * value;
      setProgress(value);
    }
  };

  // Early return for default player
  if (defaultPlayer) {
    return (
      <StyledSpacer>
        <audio controls src={url}>
          Your browser does not support the audio element.
        </audio>
      </StyledSpacer>
    );
  }

  return (
    <StyledAudioPlayer>
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        data-testid="audio-element"
      >
        Your browser does not support the audio element.
      </audio>

      <StyledButton onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </StyledButton>

      <StyledSlider
        type="range"
        min="0"
        max="100"
        value={progress}
        onChange={handleSeek}
        aria-label="Audio progress"
      />
    </StyledAudioPlayer>
  );
};

export default AudioPlayerComponent;
